import React, { useEffect, useState, useRef } from 'react';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import { ExternalLink, Bookmark, LayoutGrid } from 'lucide-react';
import axiosInstance from 'service/axiosInstance';
import { API_URLS } from 'service/url';

// ─── Module-level caches to prevent duplicate API calls ───────────────────────
const internalCache = new Map<string, InternalMeta | null>();
const externalCache = new Map<string, ExternalMeta | null>();

interface InternalMeta {
  key: string;      // e.g. "WC-2408"
  title: string;
  status: string;
  statusCategory: 'TODO' | 'IN_PROGRESS' | 'DONE' | string;
  type: 'work-item' | 'project' | 'dashboard';
}

interface ExternalMeta {
  title: string;
  icon?: string;
  favicon?: string; // fallback
  image?: string;
  description?: string;
}

// ─── URL parsing helper ────────────────────────────────────────────────────────
/**
 * Given a Worklap app URL, returns { orgSlug, projectKey, workItemKey }
 * Pattern: /{orgSlug}/pmo/{projectKey}/{workItemKey}
 */
function parseInternalUrl(href: string): {
  orgSlug: string;
  projectKey: string;
  workItemKey?: string;
  kind: 'work-item' | 'project' | 'dashboard';
} | null {
  try {
    const url = new URL(href);
    const parts = url.pathname.split('/').filter(Boolean);
    // parts: [orgSlug, 'pmo', projectKey?, segment?]
    const pmoIdx = parts.indexOf('pmo');
    if (pmoIdx === -1) return null;

    const orgSlug = parts[pmoIdx - 1];
    const projectKey = parts[pmoIdx + 1]; // e.g. "WC" or "PROJ"
    const segment = parts[pmoIdx + 2];    // e.g. "WC-2408", "backlog", "board", undefined

    if (!projectKey || projectKey === 'projects' || projectKey === 'create' || projectKey === 'edit') {
      return { orgSlug, projectKey: '', kind: 'dashboard' };
    }

    const NON_TASK_SEGMENTS = ['backlog', 'board', 'create-task', 'settings', 'reports'];
    if (segment && !NON_TASK_SEGMENTS.includes(segment)) {
      // Looks like a work item key (e.g., "WC-2408")
      return { orgSlug, projectKey, workItemKey: segment, kind: 'work-item' };
    }

    return { orgSlug, projectKey, kind: 'project' };
  } catch {
    return null;
  }
}

// ─── Status badge colour mapping ──────────────────────────────────────────────
function getStatusStyle(category: string) {
  switch (category) {
    case 'IN_PROGRESS':
      return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    case 'DONE':
      return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
    default: // TODO
      return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
}

// ─── Skeleton shimmer ─────────────────────────────────────────────────────────
const Shimmer = () => (
  <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-white/10 bg-white/5 animate-pulse">
    <span className="w-4 h-4 rounded-full bg-white/10" />
    <span className="w-28 h-3 rounded bg-white/10" />
  </span>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const SmartLinkComponent: React.FC<NodeViewProps> = ({ node, updateAttributes }) => {
  const { href } = node.attrs;
  const [internalMeta, setInternalMeta] = useState<InternalMeta | null | undefined>(undefined);
  const [externalMeta, setExternalMeta] = useState<ExternalMeta | null | undefined>(undefined);
  const [isInternal, setIsInternal] = useState<boolean | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!href) return;

    let cancelled = false;
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    const resolve = async () => {
      try {
        const url = new URL(href);
        const isInternalHost =
          url.hostname.includes('worklap.com') ||
          url.hostname === 'localhost';

        setIsInternal(isInternalHost);

        if (isInternalHost) {
          // ── Internal link ──────────────────────────────────────
          const parsed = parseInternalUrl(href);
          if (!parsed) {
            setInternalMeta(null);
            return;
          }

          if (parsed.kind === 'dashboard') {
            const meta: InternalMeta = {
              key: 'PMO',
              title: 'PMO Dashboard',
              status: '',
              statusCategory: 'TODO',
              type: 'dashboard',
            };
            setInternalMeta(meta);
            updateAttributes({ title: meta.title, isInternal: 'true' });
            return;
          }

          if (parsed.kind === 'project') {
            // Check cache first
            const cacheKey = `project:${parsed.orgSlug}/${parsed.projectKey}`;
            if (internalCache.has(cacheKey)) {
              const meta = internalCache.get(cacheKey)!;
              setInternalMeta(meta);
              updateAttributes({ title: meta.title, isInternal: 'true' });
              return;
            }
            // Resolve project key → UUID
            const resolveRes = await axiosInstance.get(
              API_URLS.RESOLVE_PROJECT(parsed.orgSlug, parsed.projectKey),
            );
            const projectUuid: string | undefined = resolveRes.data?.response?.projectUuid;
            if (!projectUuid || cancelled) return;

            // Fetch project details
            const projectRes = await axiosInstance.get(
              `${API_URLS.PROJECT_BY_ID}/${projectUuid}`,
            );
            const project = projectRes.data?.response;
            if (!project || cancelled) return;

            const meta: InternalMeta = {
              key: parsed.projectKey,
              title: project.name || parsed.projectKey,
              status: project.projectStatus || '',
              statusCategory: 'TODO',
              type: 'project',
            };
            internalCache.set(cacheKey, meta);
            setInternalMeta(meta);
            updateAttributes({ title: meta.title, isInternal: 'true' });
            return;
          }

          if (parsed.kind === 'work-item' && parsed.workItemKey) {
            const cacheKey = `work-item:${parsed.orgSlug}/${parsed.projectKey}/${parsed.workItemKey}`;
            if (internalCache.has(cacheKey)) {
              const meta = internalCache.get(cacheKey)!;
              setInternalMeta(meta);
              updateAttributes({ title: `${meta.key}: ${meta.title}`, isInternal: 'true' });
              return;
            }

            // Step 1: resolve key → UUID
            const resolveRes = await axiosInstance.get(
              API_URLS.RESOLVE_WORK_ITEM(parsed.orgSlug, parsed.projectKey, parsed.workItemKey),
            );
            const resolved = resolveRes.data?.response;
            if (!resolved?.projectUuid || !resolved?.workItemUuid || cancelled) return;

            // Step 2: get work item details
            const detailRes = await axiosInstance.get(
              `pmo/project/${resolved.projectUuid}/get-work-item/${resolved.workItemUuid}`,
            );
            const task = detailRes.data?.response;
            if (!task || cancelled) return;

            const meta: InternalMeta = {
              key: task.workItemKey || parsed.workItemKey,
              title: task.title || task.workItemTitle || parsed.workItemKey,
              status: task.workStatusName || '',
              statusCategory: task.workStatusCategory || 'TODO',
              type: 'work-item',
            };
            internalCache.set(cacheKey, meta);
            setInternalMeta(meta);
            updateAttributes({ title: `${meta.key}: ${meta.title}`, isInternal: 'true' });
          }
        } else {
          // ── External link ──────────────────────────────────────
          if (externalCache.has(href)) {
            const meta = externalCache.get(href)!;
            setExternalMeta(meta);
            updateAttributes({ 
              title: meta.title, 
              icon: meta.icon || meta.favicon, 
              isInternal: 'false' 
            });
            return;
          }

          // ── Backend API Metadata Resolution ──────────────────
          const res = await axiosInstance.get(`${API_URLS.GET_URL_METADATA}?url=${encodeURIComponent(href)}`);
          if (cancelled) return;

          const data = res.data?.response; 
          const meta: ExternalMeta = {
            title: data?.title || url.hostname.replace('www.', ''),
            icon: data?.icon || data?.logo || data?.favicon || data?.image,
            favicon: data?.favicon || data?.logo,
            image: data?.image,
            description: data?.description,
          };
          externalCache.set(href, meta);
          setExternalMeta(meta);
          
          updateAttributes({ 
            title: meta.title, 
            icon: meta.icon || meta.favicon, 
            isInternal: 'false' 
          });
        }
      } catch {
        if (cancelled) return;
        // Graceful fallback — show hostname for external, key for internal
        if (isInternal) {
          setInternalMeta(null);
        } else {
          try {
            const url = new URL(href);
            const title = url.hostname.replace('www.', '');
            setExternalMeta({ title });
            updateAttributes({ title, isInternal: 'false' });
          } catch {
            setExternalMeta({ title: href });
            updateAttributes({ title: href, isInternal: 'false' });
          }
        }
      }
    };

    resolve();

    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [href]);

  // ── Loading state ────────────────────────────────────────────────────────────
  const isLoading =
    (isInternal === true && internalMeta === undefined) ||
    (isInternal === false && externalMeta === undefined);

  if (isLoading) {
    return (
      <NodeViewWrapper className="inline-block align-middle mx-1 my-0.5" contentEditable={false}>
        <Shimmer />
      </NodeViewWrapper>
    );
  }

  // ── Internal: Work Item ──────────────────────────────────────────────────────
  if (isInternal && internalMeta?.type === 'work-item') {
    return (
      <NodeViewWrapper className="inline-block align-middle mx-1 my-0.5" contentEditable={false} style={{ verticalAlign: '-4px' }}>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-1.5 pl-2 pr-2 h-[26px] rounded border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 transition-all duration-200 !no-underline shadow-sm"
          title={`${internalMeta.key}: ${internalMeta.title}`}
        >
          {/* Bookmark icon */}
          <span className="shrink-0 text-amber-500 flex items-center">
            <Bookmark size={12} fill="currentColor" />
          </span>

          {/* Title */}
          <span className="text-[13px] font-semibold text-blue-400 group-hover:text-blue-300 truncate max-w-[350px] leading-none">
            {internalMeta.key}: {internalMeta.title}
          </span>

          {/* Status badge */}
          {internalMeta.status && (
            <span
              className={`shrink-0 ml-0.5 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide rounded border ${getStatusStyle(internalMeta.statusCategory)}`}
            >
              {internalMeta.status}
            </span>
          )}
        </a>
      </NodeViewWrapper>
    );
  }

  // ── Internal: Project ────────────────────────────────────────────────────────
  if (isInternal && internalMeta?.type === 'project') {
    return (
      <NodeViewWrapper className="inline-block align-middle mx-1 my-0.5" contentEditable={false} style={{ verticalAlign: '-4px' }}>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-1.5 pl-2 pr-2 h-[26px] rounded border border-purple-500/20 bg-purple-500/10 hover:bg-purple-500/20 transition-all duration-200 !no-underline shadow-sm"
          title={internalMeta.title}
        >
          <span className="shrink-0 text-purple-400 flex items-center">
            <LayoutGrid size={12} />
          </span>
          <span className="text-[13px] font-semibold text-purple-300 truncate min-w-0 max-w-[200px] sm:max-w-[300px] flex-grow leading-none ml-0.5">
            {internalMeta.title}
          </span>
        </a>
      </NodeViewWrapper>
    );
  }

  // ── External link ─────────────────────────────────────────────────────────────
  const extTitle = externalMeta?.title || (() => {
    try { return new URL(href).hostname.replace('www.', ''); } catch { return href; }
  })();

  return (
    <NodeViewWrapper className="inline-block align-middle mx-1 my-0.5" contentEditable={false} style={{ verticalAlign: '-4px' }}>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="group inline-flex items-center gap-1.5 pl-2 pr-2 h-[26px] rounded border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 transition-all duration-200 !no-underline shadow-sm"
        title={extTitle}
      >
        {/* Icon wrapper */}
        <div className="shrink-0 flex items-center justify-center w-4 h-4 overflow-hidden rounded-[2px] bg-white/[0.03]">
          {externalMeta?.icon || externalMeta?.favicon ? (
            <img
              src={externalMeta.icon || externalMeta.favicon}
              alt=""
              className="max-w-full max-h-full object-contain pointer-events-none !m-0 !p-0"
              onError={(e) => { 
                (e.target as HTMLImageElement).src = ''; 
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <span className="text-blue-400 opacity-70">
              <ExternalLink size={12} />
            </span>
          )}
        </div>

        {/* Title */}
        <span className="text-[13px] font-semibold text-blue-400 group-hover:text-blue-300 truncate min-w-0 max-w-[200px] sm:max-w-[400px] flex-grow ml-0.5">
          {extTitle}
        </span>

        {/* External link indicator */}
        <span className="shrink-0 text-blue-500/40 opacity-0 group-hover:opacity-100 transition-all transform translate-x-[-4px] group-hover:translate-x-0 ml-1">
          <ExternalLink size={11} />
        </span>
      </a>
    </NodeViewWrapper>
  );
};

export default SmartLinkComponent;
